import type { Dir } from './redstone.ts';
import type { Cargo, CartSpec, MinecartLevel, SwitchBoard, Track } from './minecart.ts';

/**
 * Yards are drawn as text so they are easy to review:
 *   .  empty          - |  straight rail       ┌ ┐ └ ┘  curves       +  crossing
 *   S  switch (ids S1, S2… in reading order; trunk and branches given in the same order)
 *   R L E  depots for redstone, lapis, emerald
 * Coordinates: x from the left, y from the top, both from 0. Carts start on a rail cell.
 */
const D = { N: 0, E: 1, S: 2, W: 3 } as const satisfies Record<string, Dir>;
type Side = keyof typeof D;
const RAIL: Record<string, [Dir, Dir]> = {
  '-': [1, 3],
  '|': [0, 2],
  '┌': [2, 1],
  '┐': [2, 3],
  '└': [0, 1],
  '┘': [0, 3],
};
const DEPOT: Record<string, Cargo> = { R: 'redstone', L: 'lapis', E: 'emerald' };

/** `switches`: "trunk a b" per switch, e.g. "W E S" = enters from the west, points east, thrown south. */
export function parseYard(rows: string[], switches: string[]) {
  const track: Record<string, Track> = {};
  let n = 0;
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      const k = `${x},${y}`;
      if (RAIL[ch]) track[k] = { kind: 'rail', ends: RAIL[ch] };
      else if (ch === '+') track[k] = { kind: 'cross' };
      else if (DEPOT[ch]) track[k] = { kind: 'depot', cargo: DEPOT[ch] };
      else if (ch === 'S') {
        const [trunk, a, b] = switches[n++]!.split(' ') as [Side, Side, Side];
        track[k] = { kind: 'switch', id: `S${n}`, trunk: D[trunk], a: D[a], b: D[b] };
      } else if (ch !== '.') throw new Error(`unknown map character ${ch}`);
    }),
  );
  if (n !== switches.length) throw new Error('switch count does not match the map');
  return { width: [...rows[0]!].length, height: rows.length, track };
}

export const cartAt = (cargo: Cargo, x: number, y: number, dir: Side, tick = 0): CartSpec => ({
  cargo,
  x,
  y,
  dir: D[dir],
  tick,
});
const flip = (...ids: string[]): SwitchBoard => ({
  switches: Object.fromEntries(ids.map((id) => [id, true])),
});

export const MINECART_LEVELS: MinecartLevel[] = [
  {
    id: 'first-switch',
    version: 1,
    title: 'First switch',
    goal: 'Send the lapis cart to the lapis depot.',
    hints: [
      'Tap a switch to change which way it points.',
      'The cart takes the branch the switch points to.',
    ],
    ...parseYard(['--S--R', '..|...', '..└--L'], ['W E S']),
    carts: [cartAt('lapis', 0, 0, 'E')],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'three-sidings',
    version: 1,
    title: 'Three sidings',
    goal: 'Two switches, three depots. Get the lapis cart home.',
    hints: [
      'A switch only matters if the cart rolls into it from its trunk side.',
      'Leave the first switch alone.',
    ],
    ...parseYard(['--S-S-R', '..|.|..', '..E.└-L'], ['W E S', 'W E S']),
    carts: [cartAt('lapis', 0, 0, 'E')],
    goldChanges: 1,
    solution: flip('S2'),
  },
  {
    id: 'mind-the-buffers',
    version: 1,
    title: 'Mind the buffers',
    goal: 'The main line ends in a dead end. Find the way to the lapis depot.',
    hints: ['Where the rails stop, a cart crashes.', 'Both switches need to turn down.'],
    ...parseYard(['--S--..', '..|....', '..└-S-R', '....|..', '....└-L'], ['W E S', 'W E S']),
    carts: [cartAt('lapis', 0, 0, 'E')],
    goldChanges: 2,
    solution: flip('S1', 'S2'),
  },
  {
    id: 'push-through',
    version: 1,
    title: 'Push through',
    goal: 'Send the lapis cart to the lapis depot — with as few changes as you can.',
    hints: [
      'A cart coming in from a branch always rolls on to the trunk.',
      'The second switch does not need to change.',
    ],
    ...parseYard(['--S-R..', '..|.|..', '..└-S-L'], ['W E S', 'E N W']),
    carts: [cartAt('lapis', 0, 0, 'E')],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'crossing',
    version: 1,
    title: 'Level crossing',
    goal: 'Two carts, one crossing. Get both home without a crash.',
    hints: [
      'Press Run and watch where they meet.',
      'The loop at the top makes the redstone cart two ticks later.',
    ],
    ...parseYard(['.....|..', '.┌-┐.|..', '-S-S-+-R', '.....|..', '.....L..'], ['W E N', 'E W N']),
    carts: [cartAt('redstone', 0, 2, 'E'), cartAt('lapis', 5, 0, 'S', 3)],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'passing-loop',
    version: 1,
    title: 'Passing loop',
    goal: 'Two carts head towards each other on one line. Let them pass.',
    hints: ['Carts that meet head-on crash.', 'One cart can wait out of the way in the loop.'],
    ...parseYard(['..┌---┐..', 'R-S---S-L'], ['W E N', 'E W N']),
    carts: [cartAt('lapis', 1, 1, 'E'), cartAt('redstone', 7, 1, 'W')],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'merge',
    version: 1,
    title: 'Merge lane',
    goal: 'Two redstone carts join one line. Do not let them meet at the join.',
    hints: [
      'Two carts rolling into the same rail on the same tick crash.',
      'Send one of them the long way round.',
    ],
    ...parseYard(
      ['...|...', '...|...', '-SSS--R', '.||....', '.└┘....'],
      ['W E S', 'E W S', 'E W N'],
    ),
    carts: [cartAt('redstone', 0, 2, 'E'), cartAt('redstone', 3, 0, 'S', 1)],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'loading-spot',
    version: 1,
    title: 'Loading spot',
    goal: 'A second cart is loaded onto the line at tick 3. Keep that spot clear.',
    hints: [
      'A cart cannot be loaded onto a rail another cart is rolling onto.',
      'The lower line takes longer.',
    ],
    ...parseYard(['-S---SL', '.|...|.', '.└---┘.'], ['W E S', 'E W S']),
    carts: [cartAt('lapis', 0, 0, 'E'), cartAt('lapis', 3, 0, 'E', 3)],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'loop-and-crossing',
    version: 1,
    title: 'Loop and crossing',
    goal: 'Let the lapis and redstone carts pass, and miss the emerald cart at the crossing.',
    hints: [
      'Either cart could use the loop. Only one choice also misses the emerald cart.',
      'Count the ticks until each cart reaches the crossing.',
    ],
    ...parseYard(['..┌---┐.|..', 'R-S---S-+-L', '........|..', '........E..'], ['W E N', 'E W N']),
    carts: [
      cartAt('lapis', 1, 1, 'E'),
      cartAt('redstone', 9, 1, 'W'),
      cartAt('emerald', 8, 0, 'S', 6),
    ],
    goldChanges: 1,
    solution: flip('S1'),
  },
  {
    id: 'two-crossings',
    version: 1,
    title: 'Two crossings',
    goal: 'The redstone cart must slip past both of the others.',
    hints: [
      'Each loop makes the redstone cart two ticks later.',
      'Missing the first cart is not enough — check the second crossing too.',
    ],
    ...parseYard(
      ['....|..|..', '.┌-┐|..|..', '-S-S+SS+-R', '....|└┘|..', '....L..E..'],
      ['W E N', 'E W N', 'W E S', 'E W S'],
    ),
    carts: [
      cartAt('redstone', 0, 2, 'E'),
      cartAt('lapis', 4, 0, 'S', 2),
      cartAt('emerald', 7, 0, 'S', 7),
    ],
    goldChanges: 2,
    solution: flip('S1', 'S3'),
  },
  {
    id: 'rush-hour',
    version: 1,
    title: 'Rush hour',
    goal: 'Three redstone carts, two joins, one depot. No crashes.',
    hints: ['A cart in a loop is four ticks later.', 'The second loop is used by two carts.'],
    ...parseYard(
      ['...|...|..', '...|...|..', '-SSS-SSS-R', '.||..||...', '.└┘..└┘...'],
      ['W E S', 'E W S', 'E W N', 'W E S', 'E W S', 'E W N'],
    ),
    carts: [
      cartAt('redstone', 0, 2, 'E'),
      cartAt('redstone', 3, 0, 'S', 1),
      cartAt('redstone', 7, 0, 'S', 5),
    ],
    goldChanges: 2,
    solution: flip('S1', 'S4'),
  },
  {
    id: 'grand-junction',
    version: 1,
    title: 'Grand junction',
    goal: 'Three carts, three cargoes, two crossings. Bring every cart home.',
    hints: [
      'The redstone cart is the only one you can steer.',
      'It needs both loops — and the last switch is a trap.',
    ],
    ...parseYard(
      [
        '....L..|....',
        '....|..|┌-┐.',
        'R-S-+SS+S-S-',
        '..|.|└┘|....',
        '..L.|..E....',
        '....|.......',
      ],
      ['E W S', 'W E S', 'E W S', 'W E N', 'E W N'],
    ),
    carts: [
      cartAt('redstone', 11, 2, 'W'),
      cartAt('lapis', 4, 5, 'N', 6),
      cartAt('emerald', 7, 0, 'S', 2),
    ],
    goldChanges: 2,
    solution: flip('S3', 'S5'),
  },
];
